import { ChevronDownIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import InfoTooltip from 'components/layout/info-tooltip';
import { ReactNode, useState } from 'react';

type AccordionSectionProps = Readonly<{
  title: string;
  badge?: string | number | null;
  // Admin-managed info tooltip content (AHA-6681); rendered as an info icon
  // next to the title when present.
  info?: string;
  defaultOpen?: boolean;
  children: ReactNode;
}>;

export default function AccordionSection({
  title,
  badge,
  info,
  defaultOpen = false,
  children
}: AccordionSectionProps) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className='border-b border-gray-100 last:border-b-0'>
      <button
        type='button'
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className='flex w-full items-center justify-between gap-2 py-2.5 text-left'
      >
        <span className='flex items-center gap-2 text-sm font-semibold text-gray-900'>
          {title}
          {info && (
            // The header is a toggle button; interacting with the icon must
            // not also toggle the accordion.
            // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
            <span onClick={(e) => e.stopPropagation()}>
              <InfoTooltip>{info}</InfoTooltip>
            </span>
          )}
          {badge != null && badge !== 0 && (
            <span className='rounded-full bg-blue-500 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white'>
              {badge}
            </span>
          )}
        </span>
        <ChevronDownIcon
          className={clsx(
            'h-4 w-4 shrink-0 text-gray-500 transition-transform duration-200',
            open && 'rotate-180'
          )}
        />
      </button>
      <div
        className={clsx(
          'grid transition-[grid-template-rows] duration-200 ease-in-out',
          open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        )}
      >
        <div className='overflow-hidden'>
          <div className='pb-3'>{children}</div>
        </div>
      </div>
    </div>
  );
}
